import { keepPreviousData, queryOptions, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { Build, Job } from "~/components/build/Show/lib/types";
import fetcher from "~/components/build/Show/lib/fetcher";
import { setJobQueryData } from "~/components/build/Show/lib/queries/job";
import { setStepJobsQueryData } from "~/components/build/Show/lib/queries/stepJobs";

type JobsData = {
  records: Job[];
};

export const allJobsQueryKey = (buildId: Build["id"], showRetriedJobs: boolean) => {
  return [
    "build",
    buildId,
    "jobs",
    "all",
    {
      ...(Features.BuildTableHideRetriedJobs && { showRetriedJobs }),
    },
  ];
};

/**
 * Query options for fetching **all** the jobs async.
 *
 * This is temporary whilst we work towards releasing the paginated table.
 */
export function allJobsQueryOptions(
  build: Build,
  showRetriedJobs: boolean,
  queryOptionsArgs: Omit<UseQueryOptions<JobsData>, "queryKey" | "queryFn"> = {},
) {
  return queryOptions({
    queryKey: allJobsQueryKey(build.id, showRetriedJobs),
    queryFn: async ({ signal, client: queryClient }) => {
      const queryParams = new URLSearchParams([
        ["include_retried_jobs", `${Features.BuildTableHideRetriedJobs ? showRetriedJobs : true}`],
        ["paginate", "false"],
      ]);

      return fetcher<JobsData>(`${build.buildDataBasePath}/jobs?${queryParams.toString()}`, true, {
        signal,
      }).then((data) => {
        const steps = new Map<string, Job[]>();
        const jobs = data.records;

        jobs.forEach((job) => {
          // Group all the jobs by step uuid
          const stepJobs = steps.get(job.stepUuid) || [];
          stepJobs.push(job);
          steps.set(job.stepUuid, stepJobs);

          // Pre-populate the jobs/:id queries
          setJobQueryData(build.id, job, queryClient);
        });

        for (const [stepUuid, jobs] of steps) {
          // Pre-populate the steps/:stepUuid/jobs/:id queries
          setStepJobsQueryData(build.id, stepUuid, jobs, queryClient);
        }

        return data;
      });
    },
    placeholderData: keepPreviousData,
    enabled: Features.ProgressiveBuildShow && !Features.BuildTablePaginatedJobs,
    ...queryOptionsArgs,
  });
}

/**
 * Fetch **all** the jobs async.
 *
 * This is temporary whilst we work towards releasing the paginated table.
 */
export function useAllJobsQuery(
  build: Build,
  showRetriedJobs: boolean,
  options?: { enabled?: boolean },
) {
  return useQuery(allJobsQueryOptions(build, showRetriedJobs, options));
}
